import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';

// Create a dedicated axios instance for auth (before interceptors are added)
const authApiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://sakha-cms.sakhaai.test'
});

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const accessToken = localStorage.getItem('access_token');
        if (accessToken) {
          // Try to get user and tenant from storage first
          const storedUser = localStorage.getItem('user');
          const storedTenant = localStorage.getItem('tenant');

          if (storedUser && storedTenant) {
            setUser(JSON.parse(storedUser));
            setTenant(JSON.parse(storedTenant));
          }

          // Verify token by calling /api/auth/me
          try {
            const response = await authApiClient.get('/api/auth/me', {
              headers: {
                Authorization: `Bearer ${accessToken}`
              }
            });

            const payload = response.data?.data ?? response.data;
            const { user: freshUser, tenant: freshTenant } = payload;
            setUser(freshUser);
            setTenant(freshTenant);

            // Update storage with fresh data
            localStorage.setItem('user', JSON.stringify(freshUser));
            localStorage.setItem('tenant', JSON.stringify(freshTenant));
          } catch (apiError) {
            console.warn('Token verification failed, clearing auth');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            localStorage.removeItem('tenant');
            localStorage.removeItem('tenant_id');
            localStorage.removeItem('project_id');
            setUser(null);
            setTenant(null);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email, password, tenantSlug) => {
    try {
      console.log('Attempting login...');
      const response = await authApiClient.post('/api/auth/login', {
        email,
        password,
        tenantSlug
      });

      const loginPayload = response.data?.data ?? response.data;
      const { accessToken, refreshToken, user: newUser, tenant: newTenant } = loginPayload;

      // Store tokens
      localStorage.setItem('access_token', accessToken);
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }

      // Store user and tenant info
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('tenant', JSON.stringify(newTenant));
      localStorage.setItem('tenant_id', newTenant.id);

      setUser(newUser);
      setTenant(newTenant);

      console.log('Login successful');
      return { success: true, user: newUser, tenant: newTenant };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Login failed'
      };
    }
  };

  const logout = async () => {
    try {
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        try {
          await authApiClient.post('/api/auth/logout', {}, {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          });
        } catch (error) {
          console.warn('Backend logout failed, continuing with local cleanup');
        }
      }

      // Clear all tokens and user data
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('project_id');
      localStorage.removeItem('active_project_id');

      setUser(null);
      setTenant(null);

      console.log('Logout completed');
    } catch (error) {
      console.error('Error during logout:', error);
      // Force clear everything even if there were errors
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('project_id');
      localStorage.removeItem('active_project_id');
      setUser(null);
      setTenant(null);
    }
  };

  const value = {
    user,
    tenant,
    loading,
    isAuthenticated: !!user && !!localStorage.getItem('access_token'),
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { authApiClient };
